/*
 * Copyright (C) 2025 TomTom Navigation B.V.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { z } from "zod";
import { responseDetailSchema } from "../shared/responseOptions";

// UI visibility parameter for MCP Apps
export const uiVisibilityParam = {
  show_ui: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Whether to display the interactive map widget. Set to true when visualization is needed for the user. Default: false"
    ),
};

// Shared search parameter schemas
export const baseSearchParams = {
  response_detail: responseDetailSchema,

  limit: z
    .number()
    .min(1)
    .max(100)
    .optional()
    .describe("Maximum number of results to return (1-100). Default: 5"),

  language: z
    .string()
    .optional()
    .describe(
      "Preferred language for results using IETF language tags. Examples: 'en-US', 'fr-FR', 'de-DE', 'es-ES'"
    ),

  countries: z
    .array(z.string())
    .optional()
    .describe(
      "Limit results to specific countries using ISO alpha-2 codes. Example: ['US'], ['FR', 'GB'], ['NL', 'DE']"
    ),

  view: z
    .string()
    .optional()
    .describe(
      "Geopolitical view for disputed territories. Options: 'Unified', 'AR', 'IL', 'IN', 'MA', 'PK', 'RU', 'TR', 'CN'"
    ),

  extendedPostalCodesFor: z
    .string()
    .optional()
    .describe(
      "Include extended postal codes for specific index types. Examples: 'PAD', 'PAD,Addr', 'POI'"
    ),

  mapcodes: z
    .array(z.string())
    .optional()
    .describe(
      "Include mapcode information in the response. Mapcodes represent specific locations within a few meters and are designed to be short, easy to recognize and communicate. Options: Local, International, Alternative. Examples: 'Local' (local mapcode only), 'Local,Alternative' (multiple types). Accepts array of string(s)."
    ),

  timeZone: z
    .string()
    .optional()
    .describe(
      "Used to indicate the mode in which the timeZone object should be returned. Values: iana Mode shows the IANA ID which allows the user to determine the current time zone for the POI. Usage examples: timeZone=iana"
    ),
};

export const locationBiasParams = {
  position: z
    .array(z.number())
    .length(2)
    .optional()
    .describe(
      "Center position as [longitude, latitude] for location bias (GeoJSON convention). " +
        "Example: [4.89707, 52.377956] for Amsterdam."
    ),

  radius: z.number().optional().describe("Search radius in meters when position is provided"),
};

export const boundingBoxParams = {
  boundingBox: z
    .array(z.number())
    .length(4)
    .optional()
    .describe(
      "Bounding box as [minLon, minLat, maxLon, maxLat] (GeoJSON convention). " +
        "Example: [4.8, 52.3, 4.95, 52.45] for Amsterdam area."
    ),
};

export const poiFilterParams = {
  brandSet: z
    .string()
    .optional()
    .describe(
      "Filter by brand names. Examples: 'Starbucks,Peet\\'s', 'Marriott,Hilton'. Use quotes for brands with commas."
    ),

  connectorSet: z
    .string()
    .optional()
    .describe("EV connector types: 'IEC62196Type2CableAttached', 'Chademo', 'TeslaConnector'"),

  fuelSet: z
    .string()
    .optional()
    .describe("Fuel types: 'Petrol', 'Diesel', 'LPG', 'Hydrogen', 'E85'"),

  vehicleTypeSet: z
    .string()
    .optional()
    .describe(
      "A comma-separated list of vehicle types that could be used to restrict the result to the Points Of Interest of specific vehicles. If vehicleTypeSet is specified, the query can remain empty. Only POIs with a proper vehicle type will be returned. Value: A comma-separated list of vehicle type identifiers (in any order). When multiple vehicles types are provided, only POIs that belong to (at least) one of the vehicle types from the provided list will be returned. Available vehicle types: Car , Truck"
    ),

  minPowerKW: z.number().optional().describe("Minimum charging power in kW for EV stations"),

  maxPowerKW: z.number().optional().describe("Maximum charging power in kW for EV stations"),

  openingHours: z
    .string()
    .optional()
    .describe(
      "List of opening hours for a POI (Points of Interest).Value: `nextSevenDays` Mode shows the opening hours for next week, starting with the current day in the local time of the POI. Usage example: openingHours=nextSevenDays"
    ),
};
